// src/components/Pentests/tabs/PreEngagement.js
//
// Phase 1 of the 2026-05-14 product push: replaces the Overview + Questionnaire
// tabs with a single Pre-Engagement page. Renders 8 sections: Status, Scope,
// Test Window, Rules of Engagement, Points of Contact, Test Objectives,
// Success Criteria, Data Handling. The Status Page editor (external report
// URL + notes) lives at the bottom, followed by a collapsed read-only
// "Legacy questionnaire notes" panel that only appears when the d7e8f9a0b1c2
// migration backfilled prior questionnaire answers into pre_engagement.legacy_notes.
//
// Status and Scope still read/write to PT.status / PT.scope_targets - same
// existing columns and same wire shape. Everything else lives in the new
// PT.pre_engagement JSONB column.

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight, ChevronUp, ExternalLink, Plus, RefreshCw, Star, Trash2 } from 'lucide-react';
import { pentestsAPI } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { ChipInput, PrimaryCTA } from '../../common/Shinobi';
import { RULE_SUGGESTIONS, RuleInput } from '../RuleInput';
import { StatusSelect } from '../PentestStatus';
import ScopeTargetLevels from '../ScopeTargetLevels';
import IdentityVault from '../IdentityVault';
import KnownFacts from '../KnownFacts';
import { formatAbsoluteDate } from '../../../utils/time';

const isValidIP = (v) =>
  /^(?:\d{1,3}\.){3}\d{1,3}(?:\/\d{1,2})?$/.test(v) ||
  /^[0-9a-fA-F:]+(?:\/\d{1,3})?$/.test(v);
const isValidHost = (v) => /^[a-zA-Z0-9][\w.-]*\.[a-zA-Z]{2,}(?:\/.*)?$/.test(v);
const isValidURL = (v) => /^https?:\/\/[^\s]+$/.test(v) || isValidHost(v) || isValidIP(v);

const EMPTY_PRE_ENGAGEMENT = {
  test_window: { allowed_times: '', blackout_dates: [], notes: '' },
  rules_of_engagement: {
    allowed: [], excluded: [], notes: '',
    // Agentic-mode policy. The backend defaults these too, so an older pentest
    // with none of them set still resolves to the safe values.
    exploit_level_floor: 'confirm', rate_limit_rps: 5,
    max_concurrent_agents: 4, forbidden_actions: [],
  },
  points_of_contact: [],
  test_objectives: [],
  success_criteria: '',
  data_handling: { storage_location: '', retention_days: null, notes: '' },
  legacy_notes: '',
};

function withDefaults(pe) {
  if (!pe) return { ...EMPTY_PRE_ENGAGEMENT };
  return {
    test_window: {
      allowed_times: pe.test_window?.allowed_times ?? '',
      blackout_dates: Array.isArray(pe.test_window?.blackout_dates) ? pe.test_window.blackout_dates : [],
      notes: pe.test_window?.notes ?? '',
    },
    rules_of_engagement: {
      allowed: Array.isArray(pe.rules_of_engagement?.allowed) ? pe.rules_of_engagement.allowed : [],
      excluded: Array.isArray(pe.rules_of_engagement?.excluded) ? pe.rules_of_engagement.excluded : [],
      notes: pe.rules_of_engagement?.notes ?? '',
      exploit_level_floor: pe.rules_of_engagement?.exploit_level_floor ?? 'confirm',
      rate_limit_rps: pe.rules_of_engagement?.rate_limit_rps ?? 5,
      max_concurrent_agents: pe.rules_of_engagement?.max_concurrent_agents ?? 4,
      forbidden_actions: Array.isArray(pe.rules_of_engagement?.forbidden_actions)
        ? pe.rules_of_engagement.forbidden_actions : [],
    },
    points_of_contact: Array.isArray(pe.points_of_contact) ? pe.points_of_contact : [],
    test_objectives: Array.isArray(pe.test_objectives) ? pe.test_objectives : [],
    success_criteria: pe.success_criteria ?? '',
    data_handling: {
      storage_location: pe.data_handling?.storage_location ?? '',
      retention_days: pe.data_handling?.retention_days ?? null,
      notes: pe.data_handling?.notes ?? '',
    },
    legacy_notes: pe.legacy_notes ?? '',
  };
}

/**
 * A section that can fold, and that says what is inside it while folded.
 *
 * The page was eleven full-height cards stacked vertically, most of them empty
 * on any given engagement, so the two fields an operator came to change sat a
 * screen and a half below the fold with nothing but whitespace above them.
 *
 * FOLDING ALONE WOULD NOT FIX IT, and would arguably make it worse: a row of
 * identical closed headers hides the thing you are looking for just as
 * effectively as a screen of whitespace. So a collapsed section carries a
 * `summary` of what it holds, which is also what makes an empty section
 * skippable at a glance rather than something to open and check.
 *
 * Sections holding nothing start closed; sections holding something start open,
 * because on a configured engagement the content IS the navigation.
 */
function SectionCard({
  id, title, description, children, accent = 'default', summary, defaultOpen = true,
}) {
  const [open, setOpen] = useState(defaultOpen);
  const accentClass = accent === 'amber'
    ? 'border-amber-300 bg-amber-500/5'
    : 'border-cyber-300/40 bg-cyber-50/60';
  return (
    <section id={id} className={`scroll-mt-20 rounded-xl border ${accentClass}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-start gap-3 px-4 py-3 text-left"
      >
        <ChevronRight
          className={`mt-0.5 h-4 w-4 shrink-0 text-slate-400 transition-transform ${open ? 'rotate-90' : ''}`}
        />
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-baseline gap-x-2">
            <h3 className="text-base font-semibold text-slate-900">{title}</h3>
            {!open && summary && (
              <span className="truncate text-xs text-slate-500">{summary}</span>
            )}
          </span>
          {open && description && (
            <p className="mt-0.5 text-xs text-slate-500">{description}</p>
          )}
        </span>
      </button>
      {open && <div className="px-4 pb-4">{children}</div>}
    </section>
  );
}

/**
 * Jump links. Eleven sections is too many to scroll through looking for one,
 * and the anchor targets carry `scroll-mt-20` so a jump does not land the
 * heading under the sticky tab bar.
 */
function SectionNav({ sections }) {
  return (
    <nav className="sticky top-0 z-20 -mx-1 mb-1 flex flex-wrap gap-x-3 gap-y-1 border-b border-slate-200 bg-white/95 px-1 py-2 backdrop-blur">
      {sections.map((s) => (
        <a
          key={s.id}
          href={`#${s.id}`}
          className="text-xs font-medium text-slate-500 hover:text-wasabi-700 hover:underline"
        >
          {s.label}
        </a>
      ))}
    </nav>
  );
}

function FieldLabel({ children }) {
  return <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1.5">{children}</label>;
}

function TextInput({ value, onChange, placeholder, disabled, type = 'text' }) {
  return (
    <input
      type={type}
      value={value ?? ''}
      onChange={(e) => onChange(type === 'number' ? (e.target.value === '' ? null : Number(e.target.value)) : e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
      className="w-full bg-cyber-100 border border-cyber-300/60 rounded-lg px-3 py-2 text-slate-900 outline-none focus:border-shinobi-green text-sm disabled:opacity-60"
    />
  );
}

function TextArea({ value, onChange, placeholder, disabled, rows = 3 }) {
  return (
    <textarea
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
      rows={rows}
      className="w-full bg-cyber-100 border border-cyber-300/60 rounded-lg px-3 py-2 text-slate-900 outline-none focus:border-shinobi-green text-sm disabled:opacity-60"
    />
  );
}

function PointOfContactRow({ poc, onChange, onRemove, disabled }) {
  const update = (field, value) => onChange({ ...poc, [field]: value });
  return (
    <div className="rounded-lg border border-cyber-300/40 bg-cyber-100/60 p-3 space-y-2">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => update('is_escalation', !poc.is_escalation)}
          disabled={disabled}
          title={poc.is_escalation ? 'Marked as escalation contact' : 'Mark as escalation contact'}
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] uppercase tracking-wider font-bold transition ${
            poc.is_escalation
              ? 'bg-amber-100 text-amber-700 border-amber-300'
              : 'bg-cyber-200/40 text-slate-500 border-cyber-300/40 hover:text-slate-800'
          }`}
        >
          <Star className="w-3 h-3" />
          {poc.is_escalation ? 'Escalation' : 'Standard'}
        </button>
        <div className="flex-1" />
        {!disabled && (
          <button
            type="button"
            onClick={onRemove}
            className="text-slate-500 hover:text-rose-700 transition"
            aria-label="Remove contact"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
      <div className="grid gap-2 md:grid-cols-2">
        <TextInput value={poc.name} onChange={(v) => update('name', v)} placeholder="Name" disabled={disabled} />
        <TextInput value={poc.role} onChange={(v) => update('role', v)} placeholder="Role / team" disabled={disabled} />
        <TextInput value={poc.email} onChange={(v) => update('email', v)} placeholder="email@wasabi.com" disabled={disabled} />
        <TextInput value={poc.phone} onChange={(v) => update('phone', v)} placeholder="+1 ..." disabled={disabled} />
      </div>
    </div>
  );
}

function TestObjectiveRow({ value, onChange, onRemove, disabled, idx }) {
  return (
    <div className="flex items-start gap-2">
      <span className="text-xs uppercase tracking-wider text-slate-500 font-mono mt-2 shrink-0">#{idx + 1}</span>
      <TextArea value={value} onChange={onChange} placeholder="e.g. Validate that perimeter monitoring detects port-scan activity within 5 minutes" disabled={disabled} rows={2} />
      {!disabled && (
        <button
          type="button"
          onClick={onRemove}
          className="text-slate-500 hover:text-rose-700 transition mt-2"
          aria-label="Remove objective"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

function LegacyNotesPanel({ markdown }) {
  const [open, setOpen] = useState(false);
  if (!markdown || !markdown.trim()) return null;
  return (
    <section className="rounded-xl border border-purple-300 bg-purple-500/5 p-5">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 text-left w-full"
      >
        <span className="text-[10px] uppercase tracking-[0.18em] text-purple-700 font-semibold">Legacy questionnaire notes</span>
        <span className="flex-1" />
        {open ? <ChevronUp className="w-4 h-4 text-purple-700" /> : <ChevronDown className="w-4 h-4 text-purple-700" />}
      </button>
      <p className="text-xs text-slate-500 mt-1">
        Backfilled from the old Questionnaire tab so the historical scoping context is not lost. Read-only.
      </p>
      {open && (
        <pre className="mt-3 max-h-96 overflow-auto whitespace-pre-wrap text-sm text-slate-800 leading-relaxed bg-cyber-100/60 border border-cyber-300/40 rounded-lg p-3 font-sans">
          {markdown}
        </pre>
      )}
    </section>
  );
}

export default function PreEngagement({ pentest, onUpdated }) {
  const { isPentester } = useAuth();

  const [scope, setScope] = useState(pentest.scope_targets);
  const [status, setStatus] = useState(pentest.status);
  const [pe, setPe] = useState(() => withDefaults(pentest.pre_engagement));
  const [externalReportUrl, setExternalReportUrl] = useState(pentest.external_report_url || '');
  const [notes, setNotes] = useState(pentest.notes || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // Bumped after a successful save so the level list refetches. Saving can
  // change scope targets AND the floor, either of which alters the effective
  // level of every inherited target.
  const [savedAt, setSavedAt] = useState(0);

  const originalPe = useMemo(() => withDefaults(pentest.pre_engagement), [pentest.pre_engagement]);

  // Collapsed-state summaries. A folded section has to say what is inside it
  // or the fold just moves the problem.
  const scopeSummary = (() => {
    // `scope` is the live editing state seeded from pentest.scope_targets, NOT
    // pe. Reading it off `pe` compiled fine and always said "nothing in scope".
    const n = (scope?.urls?.length || 0)
      + (scope?.ips?.length || 0)
      + (scope?.hostnames?.length || 0);
    return n ? `${n} target${n === 1 ? '' : 's'}` : 'nothing in scope yet';
  })();
  const roeSummary = (() => {
    const r = pe.rules_of_engagement || {};
    const bits = [];
    if (r.allowed?.length) bits.push(`${r.allowed.length} allowed`);
    if (r.excluded?.length) bits.push(`${r.excluded.length} excluded`);
    if (r.forbidden_actions?.length) bits.push(`${r.forbidden_actions.length} forbidden`);
    bits.push(`${r.rate_limit_rps} rps`, `${r.max_concurrent_agents} agents`);
    return bits.join(' \u00b7 ');
  })();
  const [identityCount, setIdentityCount] = useState(null);
  const identitySummary = identityCount === null
    ? ''
    : (identityCount === 0
      ? 'none: every request is unauthenticated'
      : `${identityCount} identity(s)${identityCount < 2 ? ', no cross-account oracle' : ''}`);

  const [factCount, setFactCount] = useState(null);
  const factSummary = factCount === null
    ? ''
    : (factCount === 0
      ? 'nothing stated: everything must be discovered'
      : `${factCount} fact(s) the agents can cite`);

  const SECTIONS = [
    { id: 'status', label: 'Status' },
    { id: 'scope', label: 'Scope' },
    { id: 'window', label: 'Window' },
    { id: 'roe', label: 'Rules' },
    { id: 'levels', label: 'Levels' },
    { id: 'identities', label: 'Identities' },
    { id: 'facts', label: 'Known facts' },
    { id: 'contacts', label: 'Contacts' },
    { id: 'objectives', label: 'Objectives' },
    { id: 'success', label: 'Success' },
    { id: 'data', label: 'Data' },
    { id: 'statuspage', label: 'Status page' },
  ];

  const dirty =
    JSON.stringify(scope) !== JSON.stringify(pentest.scope_targets) ||
    status !== pentest.status ||
    JSON.stringify(pe) !== JSON.stringify(originalPe) ||
    externalReportUrl !== (pentest.external_report_url || '') ||
    notes !== (pentest.notes || '');

  const onScopeChange = (key, value) => setScope({ ...scope, [key]: value });

  const updatePe = (patch) => setPe((prev) => ({ ...prev, ...patch }));
  const updatePeNested = (section, patch) =>
    setPe((prev) => ({ ...prev, [section]: { ...prev[section], ...patch } }));

  const addPoc = () =>
    updatePe({
      points_of_contact: [
        ...pe.points_of_contact,
        { name: '', role: '', email: '', phone: '', is_escalation: false },
      ],
    });
  const updatePoc = (idx, next) => {
    const list = pe.points_of_contact.map((p, i) => (i === idx ? next : p));
    updatePe({ points_of_contact: list });
  };
  const removePoc = (idx) =>
    updatePe({ points_of_contact: pe.points_of_contact.filter((_, i) => i !== idx) });

  const addObjective = () => updatePe({ test_objectives: [...pe.test_objectives, ''] });
  const updateObjective = (idx, value) => {
    const list = pe.test_objectives.map((o, i) => (i === idx ? value : o));
    updatePe({ test_objectives: list });
  };
  const removeObjective = (idx) =>
    updatePe({ test_objectives: pe.test_objectives.filter((_, i) => i !== idx) });

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const updated = await pentestsAPI.update(pentest.id, {
        scope_targets: scope,
        status,
        pre_engagement: pe,
        external_report_url: externalReportUrl.trim() || null,
        notes: notes.trim() || null,
      });
      onUpdated(updated);
      setSavedAt((n) => n + 1);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {pentest.parent_pentest_id && (
        <Link
          to={`/pentests/${pentest.parent_pentest_id}/pre-engagement`}
          className="flex items-center gap-2 text-xs uppercase tracking-[0.16em] text-shinobi-green hover:underline"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          Retest of pentest #{pentest.parent_pentest_id}
        </Link>
      )}

      <SectionNav sections={SECTIONS} />

      <SectionCard id="status" title="Status & Lifecycle" summary={`${status}`}>
        <div className="flex flex-wrap items-center gap-4">
          <StatusSelect value={status} onChange={setStatus} disabled={!isPentester} />
          <span className="text-sm text-slate-500">
            Created {formatAbsoluteDate(pentest.created_at)}
          </span>
        </div>
      </SectionCard>

      <SectionCard
        id="scope"
        summary={scopeSummary}
        title="Scope"
        description="In-scope targets across URLs, IPs, and hostnames. Press Enter or comma to add. Out-of-scope items belong in Rules of Engagement -> Excluded."
      >
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <FieldLabel>URLs</FieldLabel>
            <ChipInput
              variant="cyan"
              values={scope.urls || []}
              onChange={(next) => onScopeChange('urls', next)}
              disabled={!isPentester}
              placeholder="https://app.wasabi.com"
              emptyHint="No URLs in scope"
              validate={isValidURL}
            />
          </div>
          <div>
            <FieldLabel>IP addresses</FieldLabel>
            <ChipInput
              variant="purple"
              values={scope.ips || []}
              onChange={(next) => onScopeChange('ips', next)}
              disabled={!isPentester}
              placeholder="10.0.1.4 or 192.168.0.0/24"
              emptyHint="No IPs in scope"
              validate={isValidIP}
            />
          </div>
          <div>
            <FieldLabel>Hostnames</FieldLabel>
            <ChipInput
              variant="pink"
              values={scope.hostnames || []}
              onChange={(next) => onScopeChange('hostnames', next)}
              disabled={!isPentester}
              placeholder="api.wasabi.com"
              emptyHint="No hostnames in scope"
              validate={isValidHost}
            />
          </div>
          <div>
            <FieldLabel>Notes</FieldLabel>
            <TextArea
              value={scope.notes || ''}
              onChange={(v) => onScopeChange('notes', v)}
              placeholder="Caveats, OOPS targets, special access, etc."
              disabled={!isPentester}
            />
          </div>
        </div>
      </SectionCard>

      <SectionCard
        id="window"
        defaultOpen={!!(pe.test_window?.allowed_times || '').trim()
          || (pe.test_window?.blackout_dates || []).length > 0}
        summary={(pe.test_window?.allowed_times || '').trim() || 'any time'}
        title="Test Window"
        description="When the operator is allowed to actively test. Blackout dates pause testing."
      >
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <FieldLabel>Allowed times</FieldLabel>
            <TextInput
              value={pe.test_window.allowed_times}
              onChange={(v) => updatePeNested('test_window', { allowed_times: v })}
              placeholder="Mon-Fri 09:00-17:00 CET"
              disabled={!isPentester}
            />
          </div>
          <div>
            <FieldLabel>Blackout dates</FieldLabel>
            <ChipInput
              variant="amber"
              values={pe.test_window.blackout_dates}
              onChange={(next) => updatePeNested('test_window', { blackout_dates: next })}
              disabled={!isPentester}
              placeholder="2026-06-15 or month-end-close"
              emptyHint="No blackout dates"
            />
          </div>
          <div className="md:col-span-2">
            <FieldLabel>Notes</FieldLabel>
            <TextArea
              value={pe.test_window.notes}
              onChange={(v) => updatePeNested('test_window', { notes: v })}
              placeholder="Special timing constraints, change-freeze windows, etc."
              disabled={!isPentester}
            />
          </div>
        </div>
      </SectionCard>

      <SectionCard
        id="roe"
        title="Rules of Engagement"
        summary={roeSummary}
        description="Techniques the operator sanctions or forbids, written as instructions the agents read verbatim. Not hosts: scope says where, this says what."
      >
        {/* Said plainly because it is true and the agents' own prompts say the
            same. The gate matches command patterns and knows nothing about
            these lists, so an operator who believes them enforced would write
            a prohibition here instead of removing a host from scope. */}
        <p className="mb-3 rounded-md border border-slate-200 bg-white px-3 py-2 text-[11px] leading-snug text-slate-500">
          These reach every agent as written instruction, not as enforcement.
          The exploitation gate gives them no weight. To make something
          impossible rather than merely forbidden, keep it out of Scope or lower
          the exploitation level.
        </p>
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <FieldLabel>Allowed</FieldLabel>
            <RuleInput
              variant="green"
              values={pe.rules_of_engagement.allowed}
              onChange={(next) => updatePeNested('rules_of_engagement', { allowed: next })}
              disabled={!isPentester}
              suggestions={RULE_SUGGESTIONS.allowed}
              placeholder="e.g. Authenticated testing using the stored identities"
            />
          </div>
          <div>
            <FieldLabel>Excluded</FieldLabel>
            <RuleInput
              variant="amber"
              values={pe.rules_of_engagement.excluded}
              onChange={(next) => updatePeNested('rules_of_engagement', { excluded: next })}
              disabled={!isPentester}
              suggestions={RULE_SUGGESTIONS.excluded}
              placeholder="e.g. Denial of service or stress testing"
            />
          </div>
        </div>
        <div className="mt-4">
          <FieldLabel>Notes</FieldLabel>
          <TextArea
            value={pe.rules_of_engagement.notes}
            onChange={(v) => updatePeNested('rules_of_engagement', { notes: v })}
            disabled={!isPentester}
            rows={2}
            placeholder="Anything the agents should know about how this target behaves."
          />
        </div>

        {/* Agentic policy. Rate limit and concurrency ARE enforced on every
            dispatch. Forbidden actions is not, despite sitting beside them, and
            the hint under it says so: the gate matches command patterns and has
            never read this list. */}
        <div className="mt-5 border-t border-slate-200 pt-4">
          <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
            Agentic policy
          </div>
          <div className="grid gap-4 md:grid-cols-3">
            {/* The exploitation floor MOVED to the Exploitation levels section
                below, where the per-target overrides it governs already live.
                It was three sections away from them, joined only by a scroll
                link. It was not removed: report.py discloses the floor in the
                deliverable, so hiding the control would make the report state a
                level nobody could see or choose. */}
            <div>
              <FieldLabel>Rate limit (req/sec per target)</FieldLabel>
              <TextInput
                type="number"
                value={pe.rules_of_engagement.rate_limit_rps}
                onChange={(v) => updatePeNested('rules_of_engagement', { rate_limit_rps: Number(v) || 1 })}
                disabled={!isPentester}
              />
              <p className="mt-1 text-[11px] leading-snug text-slate-400">Capped at 100.</p>
            </div>
            <div>
              <FieldLabel>Max concurrent agents</FieldLabel>
              <TextInput
                type="number"
                value={pe.rules_of_engagement.max_concurrent_agents}
                onChange={(v) => updatePeNested('rules_of_engagement', { max_concurrent_agents: Number(v) || 1 })}
                disabled={!isPentester}
              />
              <p className="mt-1 text-[11px] leading-snug text-slate-400">Capped at 16.</p>
            </div>
            <div className="md:col-span-3">
              <FieldLabel>Forbidden actions</FieldLabel>
              <RuleInput
                variant="amber"
                values={pe.rules_of_engagement.forbidden_actions}
                onChange={(next) => updatePeNested('rules_of_engagement', { forbidden_actions: next })}
                disabled={!isPentester}
                suggestions={RULE_SUGGESTIONS.forbidden}
                placeholder="e.g. Do not create, modify or delete users"
              />
              <p className="mt-1 text-[11px] leading-snug text-slate-400">
                Prohibitions beyond what the exploitation level already refuses.
                Written instruction, like the two lists above.
              </p>
            </div>
          </div>
        </div>
      </SectionCard>

      <SectionCard
        id="levels"
        summary={`floor ${pe.rules_of_engagement.exploit_level_floor}`}
        title="Exploitation levels"
        description="How far the agent may go, per target. A target with no level of its own inherits the engagement floor set in Rules of Engagement."
      >
        <ScopeTargetLevels
          pentestId={pentest.id}
          isPentester={isPentester}
          refreshKey={savedAt}
          floor={pe.rules_of_engagement.exploit_level_floor}
          onFloorChange={(v) => updatePeNested('rules_of_engagement', { exploit_level_floor: v })}
          floorDirty={dirty}
        />
      </SectionCard>

      <SectionCard
        id="identities"
        summary={identitySummary}
        title="Test identities"
        description="Stored credentials the agent may act as. Register two or more to test cross-account access control by replaying one account's request as another."
      >
        <IdentityVault
          pentestId={pentest.id}
          isPentester={isPentester}
          onCountChange={setIdentityCount}
        />
      </SectionCard>

      <SectionCard
        id="facts"
        summary={factSummary}
        title="Known facts"
        description="Things you already know about the target that a scanner would not find. The agents read these and can cite them; a fact steers where they look and never counts as proof."
      >
        <KnownFacts
          pentestId={pentest.id}
          isPentester={isPentester}
          onCountChange={setFactCount}
        />
      </SectionCard>

      <SectionCard
        id="contacts"
        defaultOpen={(pe.points_of_contact || []).length > 0}
        summary={`${(pe.points_of_contact || []).length} contact(s)`}
        title="Points of Contact"
        description="Operator-side and client-side contacts. Mark escalation contacts with the star toggle."
      >
        <div className="space-y-3">
          {pe.points_of_contact.length === 0 && (
            <div className="text-sm text-slate-500 italic">No contacts yet.</div>
          )}
          {pe.points_of_contact.map((poc, idx) => (
            <PointOfContactRow
              key={idx}
              poc={poc}
              onChange={(next) => updatePoc(idx, next)}
              onRemove={() => removePoc(idx)}
              disabled={!isPentester}
            />
          ))}
          {isPentester && (
            <button
              type="button"
              onClick={addPoc}
              className="inline-flex items-center gap-1.5 text-sm text-shinobi-green hover:underline"
            >
              <Plus className="w-4 h-4" /> Add contact
            </button>
          )}
        </div>
      </SectionCard>

      <SectionCard
        id="objectives"
        defaultOpen={(pe.test_objectives || []).length > 0}
        summary={`${(pe.test_objectives || []).length} objective(s)`}
        title="Test Objectives"
        description="3-5 short statements describing what success looks like for this engagement."
      >
        <div className="space-y-3">
          {pe.test_objectives.length === 0 && (
            <div className="text-sm text-slate-500 italic">No objectives yet.</div>
          )}
          {pe.test_objectives.map((obj, idx) => (
            <TestObjectiveRow
              key={idx}
              idx={idx}
              value={obj}
              onChange={(v) => updateObjective(idx, v)}
              onRemove={() => removeObjective(idx)}
              disabled={!isPentester}
            />
          ))}
          {isPentester && (
            <button
              type="button"
              onClick={addObjective}
              className="inline-flex items-center gap-1.5 text-sm text-shinobi-green hover:underline"
            >
              <Plus className="w-4 h-4" /> Add objective
            </button>
          )}
        </div>
      </SectionCard>

      <SectionCard
        id="success"
        defaultOpen={!!(pe.success_criteria || '').trim()}
        summary={(pe.success_criteria || '').trim() ? 'set' : 'not set'}
        title="Success Criteria"
        description="Single free-text answer to: how will we know this engagement was a success?"
      >
        <TextArea
          value={pe.success_criteria}
          onChange={(v) => updatePe({ success_criteria: v })}
          placeholder="e.g. All P0/P1 findings have a remediation plan agreed by the data owner before sign-off."
          disabled={!isPentester}
          rows={4}
        />
      </SectionCard>

      <SectionCard
        id="data"
        defaultOpen={false}
        summary={(pe.data_handling?.storage_location || '').trim() || 'not set'}
        title="Data Handling"
        description="Where engagement findings live and how long they are retained."
      >
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <FieldLabel>Storage location</FieldLabel>
            <TextInput
              value={pe.data_handling.storage_location}
              onChange={(v) => updatePeNested('data_handling', { storage_location: v })}
              placeholder="Wasabi Shinobi platform + backup snapshot in Wasabi S3"
              disabled={!isPentester}
            />
          </div>
          <div>
            <FieldLabel>Retention (days)</FieldLabel>
            <TextInput
              type="number"
              value={pe.data_handling.retention_days}
              onChange={(v) => updatePeNested('data_handling', { retention_days: v })}
              placeholder="365"
              disabled={!isPentester}
            />
          </div>
          <div className="md:col-span-2">
            <FieldLabel>Notes</FieldLabel>
            <TextArea
              value={pe.data_handling.notes}
              onChange={(v) => updatePeNested('data_handling', { notes: v })}
              placeholder="Encryption, access control, who has read access, etc."
              disabled={!isPentester}
            />
          </div>
        </div>
      </SectionCard>

      <SectionCard
        id="statuspage"
        defaultOpen={false}
        summary={(pentest.external_report_url || pentest.notes) ? 'set' : 'not set'}
        title="Status Page"
        description="Surfaced on the company-wide Wasabi Red Team Status Page. The report URL links a legacy Confluence / SharePoint document for engagements completed before this platform; notes appear next to the row in the portfolio table."
      >
        <div className="space-y-4">
          <div>
            <FieldLabel>External report URL</FieldLabel>
            {isPentester ? (
              <TextInput
                value={externalReportUrl}
                onChange={setExternalReportUrl}
                placeholder="https://wasabi.sharepoint.com/sites/redteam/..."
              />
            ) : pentest.external_report_url ? (
              <a
                href={pentest.external_report_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm text-shinobi-green hover:underline break-all"
              >
                <ExternalLink className="w-3.5 h-3.5 shrink-0" />
                Open report
              </a>
            ) : (
              <div className="text-sm text-slate-500 italic">No external report linked.</div>
            )}
            {isPentester && externalReportUrl && (
              <a
                href={externalReportUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 mt-1.5 text-xs text-shinobi-green hover:underline"
              >
                <ExternalLink className="w-3 h-3" />
                Test link (opens in new tab)
              </a>
            )}
          </div>
          <div>
            <FieldLabel>Notes</FieldLabel>
            {isPentester ? (
              <TextArea value={notes} onChange={setNotes} placeholder="Editorial context for the Status Page row." rows={3} />
            ) : pentest.notes ? (
              <div className="text-sm text-slate-700 whitespace-pre-wrap">{pentest.notes}</div>
            ) : (
              <div className="text-sm text-slate-500 italic">No notes.</div>
            )}
          </div>
        </div>
      </SectionCard>

      <LegacyNotesPanel markdown={pe.legacy_notes} />

      {isPentester && (
        <div className="flex items-center gap-3 sticky bottom-4 pt-2">
          <PrimaryCTA variant="green" loading={saving} disabled={!dirty} onClick={save}>
            Save Pre-Engagement
          </PrimaryCTA>
          {dirty && <span className="text-xs text-slate-500">unsaved changes</span>}
          {error && <div className="text-red-600 text-sm">{error}</div>}
        </div>
      )}
    </div>
  );
}
